/** Facility name or alias extracted from an assistant-issued switch command. */
export type SwitchFacilityData = {
	facility: string;
};

/** Minimal message shape needed to decide whether a timeline item is internal. */
export type InternalTimelineMessage = {
	id: number;
	type: 'user' | 'assistant';
	content?: string;
};

/**
 * Extracts the last complete frontend-handled facility switch from raw assistant XML.
 * Incomplete streamed commands are ignored so they cannot mutate application state.
 */
export function extractSwitchFacility(raw: string): SwitchFacilityData | null {
	const switchFacilityRe = /<switch_facility\b[^>]*>[\s\S]*?<\/switch_facility>/g;
	let match: RegExpExecArray | null;
	let lastBlock: string | null = null;

	// A payload can contain earlier commands, so the most recent complete block wins.
	while ((match = switchFacilityRe.exec(raw))) lastBlock = match[0];
	if (!lastBlock) return null;

	const facilityMatch = /<facility>([\s\S]*?)<\/facility>/.exec(lastBlock);
	const facility = facilityMatch?.[1]?.trim();

	return facility ? { facility } : null;
}

/** Returns whether an assistant payload contains a complete facility-switch command. */
export function isFacilitySwitchAssistantPayload(raw: string): boolean {
	return extractSwitchFacility(raw) !== null;
}

/** Accepts both current and historically malformed closing tool-response tags. */
export function isToolResponseUserPayload(raw: string): boolean {
	return /^\s*<tool_response\b[^>]*>[\s\S]*?<\/tool_response\s*>?\s*$/i.test(raw);
}

/**
 * Finds internal facility-switch transport messages so they can remain in history
 * without rendering. A full-payload user tool response is always internal; requiring
 * adjacency to the assistant command caused terminal and hydrated responses to leak.
 */
export function getHiddenFacilitySwitchMessageIds(messages: InternalTimelineMessage[]): Set<number> {
	const hiddenIds = new Set<number>();

	for (const message of messages) {
		// Hide the command sent by the assistant; it is application control data, not chat text.
		if (message.type === 'assistant' && isFacilitySwitchAssistantPayload(message.content ?? '')) {
			hiddenIds.add(message.id);
			continue;
		}

		// Hide the corresponding user-role acknowledgement returned to the assistant.
		if (message.type === 'user' && isToolResponseUserPayload(message.content ?? '')) {
			hiddenIds.add(message.id);
		}
	}

	return hiddenIds;
}
